<template>
  <Card class="text-gray-700 pt-2">
    <EventBuyBoxHeader
      :selected-date-type="
        selectedDateType === 'PUBLIC_PRIVATE'
          ? bookAsTDBToggle
            ? 'PRIVATE'
            : 'PUBLIC'
          : selectedDateType
      "
      :selected-date="selectedDate"
      :selected-ticket-quantity="selectedTicketQuantity"
      :enable-subdomain="enableSubdomain"
      :is-request-buttons-visible="eventBuyBoxVisibleElements.requestButtons"
    />

    <hr class="mt-2 mb-4" />

    <div ref="quantitySelectorRef">
      <EventBuyBoxQuantitySelector
        v-model="selectedTicketQuantity"
        :selected-date="selectedDate"
        :is-booking-as-private="isPrivate"
        :is-box-event="eventGetters.isBoxEvent(eventData)"
        :is-dates-loaded="isDatesLoaded"
      />
    </div>

    <div v-show="eventBuyBoxVisibleElements.calendar">
      <div v-if="!isMultiDate" class="flex flex-col gap-4 my-4">
        <EventBuyBoxDatePicker
          ref="datePickerRef"
          v-model="selectedDay"
          :selected-date="selectedDate"
          :selected-tickets-quantity="selectedTicketQuantity"
          @open-date-picker="onOpenDatePicker"
          @close-date-picker="onCloseDatePicker"
          @on-dates-loaded="handleDatesLoaded"
        />
        <EventBuyBoxTimeSlots
          v-model="selectedDate"
          :selected-day="selectedDay"
        />
      </div>
      <EventBuyBoxMultidateSelect
        v-else
        :event-id="eventGetters.getId(eventData)"
        @update-selected-date="(newDate) => (selectedDate = newDate)"
      />
    </div>
    <InfoBanner
      v-if="selectedDate && !isKitsPage"
      class="my-2"
      :info-text="infoBannerText"
    >
      <div
        v-if="selectedDateType === 'PUBLIC_PRIVATE'"
        class="mt-1 flex items-center text-xs"
      >
        <Toggle v-model="bookAsTDBToggle" />
        <span class="text-gray-600">
          {{
            $t('product.show.infoBanner.privateToggleLabel', {
              privateEventExtraPrice,
            })
          }}
        </span>
      </div>
    </InfoBanner>
    <EventBuyBoxAddonPicker
      v-model="selectedAddOn"
      :is-booking-private="isPrivate"
    />

    <Button
      class="w-full mt-2"
      data-test-id="add-to-cart-button"
      :disabled="shouldDisableMainButton"
      :style="style"
      @click="handleClickMainButton"
    >
      {{ mainButtonLabel }}
    </Button>

    <template
      v-if="
        eventBuyBoxVisibleElements.giftcard &&
        enableGiftcard &&
        !eventGetters.getIsTeamEvent(eventData)
      "
    >
      <hr class="mt-4 mt-3" />

      <EventBuyBoxGiftcard
        :background-color="backgroundColor"
        :text-color="textColor"
        class="mt-4"
        :enable-embedded="isEmbedded"
        @add-item-to-cart="emit('add-item-to-cart', $event)"
      />
    </template>
  </Card>
</template>

<script lang="ts" setup>
import type {
  EventCalendarDay,
  EventDate,
  EventDescription,
} from '~/composables/types/event'
import parseEventDateToCartItem from '~/utils/parseEventDateToCartItem'
import { useAlgoliaConversion } from '~/composables/algolia/useAlgoliaConversion'
import { EventBuyBoxDatePicker, EventBuyBoxQuantitySelector } from '#components'
import { useIsSubdomain } from '~/composables/useSubdomain'
import type { CartItem } from '~/composables/types/cart'

const props = defineProps<{
  enableGiftcard: boolean
  isEmbedded?: boolean
  textColor?: string
  backgroundColor?: string
  isKitsPage?: boolean
}>()

const emit = defineEmits<{
  (e: 'add-item-to-cart', cartItem: CartItem): void
  (e: 'dates-loaded'): void
  (e: 'toggle-date-picker'): void
}>()

const enableSubdomain = useIsSubdomain()
const eventBuyBoxVisibleElements = {
  calendar: true,
  requestButtons: true,
  giftcard: true,
  ...(inject('eventBuyBoxVisibleElements') as {
    calendar?: boolean
    requestButtons?: boolean
    giftcard?: boolean
  }),
}
const eventData = inject('eventData') as EventDescription

const { locale, t } = useI18n()
const route = useRoute()

const isMultiDate = computed(() =>
  eventDateGetters.isMultiDate(eventGetters.getNextInstance(eventData))
)
const datePickerRef = ref<typeof EventBuyBoxDatePicker | undefined>(undefined)
const quantitySelectorRef = ref<HTMLDivElement | undefined>(undefined)
const selectedDay = ref<EventCalendarDay | undefined>(undefined)
const selectedDate = ref<EventDate | undefined>(undefined)
const selectedDateType = computed(() => selectedDate.value?.dateType)
const isDatesLoaded = ref<boolean>(false)
const bookAsTDBToggle = ref(false)
const selectedTicketQuantity = ref(1)
const selectedAddOn = ref(undefined)

const style = computed(() => {
  return {
    ...(props.textColor && { color: `#${props.textColor}` }),
    ...(props.backgroundColor && {
      backgroundColor: `#${props.backgroundColor}`,
    }),
  }
})

const infoBannerText = computed(() => {
  if (isMultiDate.value) {
    return t('product.show.infoBanner.multidate')
  }
  if (bookAsTDBToggle.value || selectedDateType.value === 'PRIVATE') {
    return t('product.show.infoBanner.private')
  }

  return t('product.show.infoBanner.public')
})

const privateEventExtraPrice = computed(() => {
  const minPrivatePriceAmount = eventDateGetters.getMinimumPrivatePriceAmount(
    selectedDate.value
  )
  const currentTotalDatePrice =
    eventDateGetters.getPriceAmount(selectedDate.value) *
    selectedTicketQuantity.value

  const extraPrice = Math.max(minPrivatePriceAmount - currentTotalDatePrice, 0)
  if (extraPrice === 0) return ''
  const formattedExtraPrice = formatMoney(
    extraPrice / 100,
    convertLocaleIntoBCP47(locale.value),
    'EUR',
    2
  )
  return `(+${formattedExtraPrice})`
})
const { addItem, clear: clearCart } = useCartWithSubdomainLogic()
const { toggleCartSidebar } = useUiState()
const { sendAddToCartEvent } = useAlgoliaConversion()
const { toggleDateRequestSidebar } = useUiState()

const isPrivate = computed(
  () => selectedDateType.value === 'PRIVATE' || bookAsTDBToggle.value
)

const notEnoughTickets = computed(
  () =>
    selectedDate.value &&
    selectedTicketQuantity.value >
      eventDateGetters.getAvailableTicketsQuantity(selectedDate.value)
)

const shouldDisableMainButton = computed(() => {
  return !selectedDate.value
})

const mainButtonLabel = computed(() => {
  if (notEnoughTickets.value) {
    return t('product.show.checkout.tryDateRequestButtonText')
  }

  return t('product.show.checkout.book')
})

const handleClickMainButton = () => {
  if (notEnoughTickets.value) {
    toggleDateRequestSidebar(true, 'buy_box')
    return
  }
  book()
}

const onOpenDatePicker = () => {
  const offsetTop = quantitySelectorRef?.value?.offsetTop

  if (offsetTop) {
    window.scrollTo({
      top: offsetTop + 40,
      behavior: 'smooth',
    })
  }

  emit('toggle-date-picker')
}

const onCloseDatePicker = () => {
  emit('toggle-date-picker')
}

const handleDatesLoaded = async () => {
  isDatesLoaded.value = true
  await nextTick()
  emit('dates-loaded')
}

const book = () => {
  const queryId = route?.query?.qid?.toString() || ''
  const cartItem = parseEventDateToCartItem(
    eventData,
    selectedTicketQuantity,
    selectedDate as Ref<EventDate>,
    selectedAddOn,
    isPrivate.value,
    queryId
  )

  if (cartItem) {
    emit('add-item-to-cart', cartItem)

    if (props.isEmbedded) {
      clearCart()
    }

    addItem(cartItem)
    toggleCartSidebar()
  }

  sendAddToCartEvent(eventData)
}

watch(
  () => selectedDate.value,
  () => {
    if (isPrivate.value) {
      selectedTicketQuantity.value = eventDateGetters.getPrivateMinTickets(
        selectedDate.value
      )
    }
  }
)

watch(
  () => selectedDay.value,
  () => {
    const firstAvailableTicket = selectedDay.value?.dates?.find(
      (date) => eventDateGetters.getAvailableTicketsQuantity(date) > 0
    )

    if (firstAvailableTicket) selectedDate.value = firstAvailableTicket
  },
  { immediate: true, deep: true }
)
</script>
